import { IcdFinding, IcdRule, IcdRuleCode, IcdRulesContext, IcdRulesResult } from './icd-rules-models';

/**
 * The South African ICD-10 coding rules, driven by the MIT's own validity flags.
 *
 * Plain classes with no Angular dependency, so `scripts/icd-client-rules-check.js` can exercise
 * them directly. A rule that can only run inside a component is a rule nothing checks.
 *
 * ⚠ POSITION IS MEANING. `codes[0]` is the primary diagnosis. Several rules are about the first
 * position specifically, so a picker that reorders codes changes what is valid.
 *
 * ⚠ ABSENT FLAG MEANS DEFAULT. The asset omits default values to halve its size, so
 * `validPrimary === undefined` means TRUE, not unknown. Every rule below reads it that way, and
 * the check pins it — reading absence as false would flag 35,000 codes as invalid.
 */

const isPrimaryValid = (c: IcdRuleCode) => c.validPrimary !== false;
const isClinicallyUsable = (c: IcdRuleCode) => c.validClinicalUse !== false;

/** Chapter XX. Kept for the message only — validity itself comes from the MIT flag. */
const isExternalCause = (code: string) => /^[VWXY]/i.test(String(code || ''));

const isInjury = (c: IcdRuleCode) => /^[ST]/i.test(String(c.code || ''));

/** C00–D48. D50 onwards are blood and immune disorders, not neoplasms. */
const isNeoplasm = (c: IcdRuleCode) => /^(C|D[0-3]\d|D4[0-8])/i.test(String(c.code || ''));

/** M8000/0 style. Chapter XIII codes (M54.5) also start with M, so the form matters. */
const isMorphology = (c: IcdRuleCode) => !!c.isMorphology || /^M\d{4}\/\d/i.test(String(c.code || ''));

/** A partner code may repeat, once after each code it belongs to. */
export const isPartnerCode = (c: IcdRuleCode) => isExternalCause(c.code) || isMorphology(c);

/**
 * The order is the pairing: nothing else on a claim says which codes belong together.
 * A head must be followed directly by a partner; a run of partners belongs to the head before it.
 */
function unpairedHeads(codes: IcdRuleCode[], isHead: (c: IcdRuleCode) => boolean, isPartner: (c: IcdRuleCode) => boolean): number[] {
  return codes
    .map((c, i) => i)
    .filter((i) => isHead(codes[i]) && !(codes[i + 1] && isPartner(codes[i + 1])));
}

function strayPartners(codes: IcdRuleCode[], isHead: (c: IcdRuleCode) => boolean, isPartner: (c: IcdRuleCode) => boolean): number[] {
  const stray: number[] = [];
  let inRun = false;
  codes.forEach((c, i) => {
    if (isPartner(c)) {
      if (!inRun) stray.push(i);
    } else {
      inRun = isHead(c);
    }
  });
  return stray;
}

/** ICD-001 — a code that may never lead is sitting in the primary position. */
export class RuleIcd001PrimaryPosition implements IcdRule {
  readonly id = 'icd-001';
  readonly label = 'Primary position';

  validate(codes: IcdRuleCode[]): IcdFinding[] {
    if (!codes.length) return [];
    const first = codes[0];
    if (isPrimaryValid(first)) return [];

    // The reason matters more than the verdict — "not valid as primary" alone tells a
    // clinician nothing about what to do instead.
    const why = first.validAsterisk
      ? 'It is a manifestation (asterisk) code, which is only ever reported alongside the underlying condition.'
      : isExternalCause(first.code)
        ? 'It is an external cause code (chapter XX), which describes how an injury happened, not what it is.'
        : 'The South African MIT does not permit this code in the primary position.';

    return [{
      ruleId: this.id,
      ruleLabel: this.label,
      message: `${first.code} cannot be the primary diagnosis. ${why}`,
      severity: 'error',
      codeIndex: 0,
      code: first.code,
      suggestion: 'Move the condition being treated into first position and keep this one after it.',
    }];
  }
}

/** ICD-002 — the code needs a more specific child (M54.5 -> M54.55). */
export class RuleIcd002Specificity implements IcdRule {
  readonly id = 'icd-002';
  readonly label = 'Specificity';

  validate(codes: IcdRuleCode[]): IcdFinding[] {
    return codes
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => !isClinicallyUsable(c))
      .map(({ c, i }) => ({
        ruleId: this.id,
        ruleLabel: this.label,
        message: `${c.code} is a category heading, not a codeable diagnosis. South African coding requires the more specific code beneath it.`,
        severity: 'error' as const,
        codeIndex: i,
        code: c.code,
        suggestion: `Choose one of the ${c.code} sub-codes — for example the 5th-character site or laterality variant.`,
      }));
  }
}

/** ICD-003 — the code was withdrawn from the SA MIT. */
export class RuleIcd003Discontinued implements IcdRule {
  readonly id = 'icd-003';
  readonly label = 'Discontinued code';

  validate(codes: IcdRuleCode[]): IcdFinding[] {
    return codes
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => c.isDiscontinued || c.saEndDate)
      .map(({ c, i }) => ({
        ruleId: this.id,
        ruleLabel: this.label,
        message: `${c.code} was withdrawn from the South African MIT${c.saEndDate ? ` on ${c.saEndDate}` : ''}. Medical schemes are likely to reject it.`,
        severity: 'error' as const,
        codeIndex: i,
        code: c.code,
        suggestion: 'Use the current replacement code for this condition.',
      }));
  }
}

/** ICD-004 — the code is restricted to one sex and the patient is the other. */
export class RuleIcd004SexRestriction implements IcdRule {
  readonly id = 'icd-004';
  readonly label = 'Sex-specific code';

  validate(codes: IcdRuleCode[], ctx: IcdRulesContext): IcdFinding[] {
    const sex = String(ctx.sex || '').trim().toUpperCase().charAt(0);
    // Silent without a patient sex, rather than guessing. A false alarm here reads as the
    // system not knowing the patient, which erodes trust in every other finding.
    if (sex !== 'M' && sex !== 'F') return [];

    return codes
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => c.gender && String(c.gender).toUpperCase().charAt(0) !== sex)
      .map(({ c, i }) => ({
        ruleId: this.id,
        ruleLabel: this.label,
        message: `${c.code} is recorded in the MIT as ${String(c.gender).toUpperCase().charAt(0) === 'F' ? 'female' : 'male'}-only, but this patient is recorded as ${sex === 'F' ? 'female' : 'male'}.`,
        severity: 'warning' as const,
        codeIndex: i,
        code: c.code,
        suggestion: 'Check the code, or the sex recorded on the patient record.',
      }));
  }
}

/** ICD-005 — the code carries an age range the patient falls outside. */
export class RuleIcd005AgeRestriction implements IcdRule {
  readonly id = 'icd-005';
  readonly label = 'Age-specific code';

  validate(codes: IcdRuleCode[], ctx: IcdRulesContext): IcdFinding[] {
    const age = Number(ctx.age);
    if (!Number.isFinite(age) || age < 0) return [];

    const findings: IcdFinding[] = [];
    codes.forEach((c, i) => {
      const from = c.ageFrom;
      const to = c.ageTo;
      const below = from !== undefined && from !== null && age < from;
      const above = to !== undefined && to !== null && age > to;
      if (!below && !above) return;

      const range = below ? `from ${from} years` : `up to ${to} years`;
      findings.push({
        ruleId: this.id,
        ruleLabel: this.label,
        message: `${c.code} applies ${range}; this patient is ${age}.`,
        severity: 'warning',
        codeIndex: i,
        code: c.code,
        suggestion: 'Check the code, or the date of birth on the patient record.',
      });
    });
    return findings;
  }
}

/**
 * ICD-006 — an asterisk (manifestation) code with no dagger beside it.
 *
 * Warns, never completes. §4.3a: PHISC states the MIT's dagger/asterisk flags are
 * non-exhaustive, so a missing named partner is not reliably an error, and some partners are
 * ranges (`D63.0 -> C00-D48`) that cannot resolve to a single code.
 */
export class RuleIcd006DaggerAsterisk implements IcdRule {
  readonly id = 'icd-006';
  readonly label = 'Dagger / asterisk pairing';

  validate(codes: IcdRuleCode[]): IcdFinding[] {
    const present = new Set(codes.map((c) => String(c.code).toUpperCase()));
    const hasAnyDagger = codes.some((c) => c.validDagger);

    return codes
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => c.validAsterisk)
      .filter(({ c }) => {
        // Satisfied when a named partner is on the list, or — for an unnamed or range
        // partner — when any dagger code is present at all.
        const named = String(c.daggerPartners || '')
          .split(/[,;]/).map((s) => s.trim().toUpperCase()).filter(Boolean);
        if (named.length) return !named.some((p) => present.has(p));
        return !hasAnyDagger;
      })
      .map(({ c, i }) => ({
        ruleId: this.id,
        ruleLabel: this.label,
        message: `${c.code} is a manifestation (asterisk) code. It should be reported with the underlying condition that caused it${c.daggerPartners ? ` — the MIT names ${c.daggerPartners}` : ''}.`,
        severity: 'warning' as const,
        codeIndex: i,
        code: c.code,
        suggestion: 'Add the underlying (dagger) code and put it before this one.',
      }));
  }
}

/**
 * ICD-007 — an injury not directly followed by its external cause.
 *
 * Only the injury is checked: adverse-effect causes (Y40–Y59) legitimately follow conditions
 * that are not injuries.
 */
export class RuleIcd007InjuryNeedsExternalCause implements IcdRule {
  readonly id = 'icd-007';
  readonly label = 'Injury without external cause';

  validate(codes: IcdRuleCode[]): IcdFinding[] {
    const anyCause = codes.some((c) => isExternalCause(c.code));
    return unpairedHeads(codes, isInjury, (c) => isExternalCause(c.code)).map((i) => ({
      ruleId: this.id,
      ruleLabel: this.label,
      message: anyCause
        ? `${codes[i].code} is an injury code with no external cause directly after it. Each injury is paired with the V–Y code that follows it.`
        : `An injury code (${codes[i].code}) is present with no external cause code. South African coding expects chapter XX (V–Y) alongside an injury, giving both the place and the activity.`,
      severity: 'warning' as const,
      codeIndex: i,
      code: codes[i].code,
      suggestion: anyCause
        ? 'Move the V–Y code for this injury to directly after it, or repeat it if two injuries share a cause.'
        : 'Add the V–Y code describing how the injury happened, directly after the injury.',
    }));
  }
}

/** ICD-008 — the same code selected twice. */
export class RuleIcd008Duplicate implements IcdRule {
  readonly id = 'icd-008';
  readonly label = 'Duplicate code';

  validate(codes: IcdRuleCode[]): IcdFinding[] {
    const findings: IcdFinding[] = [];
    const seen = new Set<string>();
    codes.forEach((c, i) => {
      const key = String(c.code).toUpperCase();
      if (seen.has(key) && !isPartnerCode(c)) {
        findings.push({
          ruleId: this.id,
          ruleLabel: this.label,
          message: `${c.code} appears more than once.`,
          severity: 'warning',
          codeIndex: i,
          code: c.code,
          suggestion: 'Remove the duplicate — each code is reported once per encounter.',
        });
      }
      seen.add(key);
    });
    return findings;
  }
}

/** ICD-009 — an external cause with nothing for it to explain. */
export class RuleIcd009ExternalCauseAlone implements IcdRule {
  readonly id = 'icd-009';
  readonly label = 'External cause alone';

  validate(codes: IcdRuleCode[]): IcdFinding[] {
    if (!codes.length) return [];
    if (!codes.every((c) => isExternalCause(c.code))) return [];

    return [{
      ruleId: this.id,
      ruleLabel: this.label,
      message: 'Every code selected is an external cause. These describe how something happened, so at least one needs to say what was injured.',
      severity: 'error',
      codeIndex: null,
      suggestion: 'Add the injury or condition code and put it first.',
    }];
  }
}

/** ICD-010 — a neoplasm (C00–D48) and its morphology code are not side by side. */
export class RuleIcd010Morphology implements IcdRule {
  readonly id = 'icd-010';
  readonly label = 'Neoplasm and morphology';

  validate(codes: IcdRuleCode[]): IcdFinding[] {
    const heads = unpairedHeads(codes, isNeoplasm, isMorphology).map((i) => ({
      ruleId: this.id,
      ruleLabel: this.label,
      message: `${codes[i].code} is a neoplasm with no morphology code (M-code) directly after it.`,
      severity: 'warning' as const,
      codeIndex: i,
      code: codes[i].code,
      suggestion: 'Add the morphology code describing the tissue type directly after the neoplasm.',
    }));
    const strays = strayPartners(codes, isNeoplasm, isMorphology).map((i) => ({
      ruleId: this.id,
      ruleLabel: this.label,
      message: `${codes[i].code} is a morphology (M-code) describing tissue type, not a diagnosis. It belongs directly after the neoplasm it describes.`,
      severity: 'warning' as const,
      codeIndex: i,
      code: codes[i].code,
      suggestion: 'Move it to directly after its neoplasm (C00–D48) code.',
    }));
    return [...heads, ...strays].sort((a, b) => a.codeIndex - b.codeIndex);
  }
}

/** ICD-011 — exactly one primary. Informational: the picker treats position 1 as primary. */
export class RuleIcd011SinglePrimary implements IcdRule {
  readonly id = 'icd-011';
  readonly label = 'Primary diagnosis';

  validate(codes: IcdRuleCode[]): IcdFinding[] {
    if (codes.length === 0) return [];
    // Only meaningful once there is a choice to get wrong.
    if (codes.length === 1) return [];
    if (isPrimaryValid(codes[0])) return [];
    // ICD-001 already reports the first position; this adds the way out when a valid
    // alternative is sitting further down the list.
    const alternative = codes.find((c, i) => i > 0 && isPrimaryValid(c));
    if (!alternative) return [];

    return [{
      ruleId: this.id,
      ruleLabel: this.label,
      message: `${alternative.code} can lead and ${codes[0].code} cannot.`,
      severity: 'info',
      codeIndex: codes.indexOf(alternative),
      code: alternative.code,
      suggestion: `Move ${alternative.code} to first position.`,
    }];
  }
}

export const ICD_RULES: IcdRule[] = [
  new RuleIcd001PrimaryPosition(),
  new RuleIcd002Specificity(),
  new RuleIcd003Discontinued(),
  new RuleIcd004SexRestriction(),
  new RuleIcd005AgeRestriction(),
  new RuleIcd006DaggerAsterisk(),
  new RuleIcd007InjuryNeedsExternalCause(),
  new RuleIcd008Duplicate(),
  new RuleIcd009ExternalCauseAlone(),
  new RuleIcd010Morphology(),
  new RuleIcd011SinglePrimary(),
];

/** Runs every rule and aggregates. Never throws — one bad rule must not blank the whole panel. */
export function runIcdRules(
  codes: IcdRuleCode[],
  ctx: IcdRulesContext = {},
  rules: IcdRule[] = ICD_RULES
): IcdRulesResult {
  const list = Array.isArray(codes) ? codes.filter(Boolean) : [];
  const findings: IcdFinding[] = [];

  for (const rule of rules) {
    try {
      findings.push(...(rule.validate(list, ctx) || []));
    } catch {
      // A rule that throws is a bug in that rule, not a reason to hide the other ten.
    }
  }

  const rank = { error: 0, warning: 1, info: 2 } as const;
  findings.sort((a, b) => rank[a.severity] - rank[b.severity]);

  const overallStatus = findings.some((f) => f.severity === 'error') ? 'error'
    : findings.some((f) => f.severity === 'warning') ? 'warning'
      : 'valid';

  return { findings, overallStatus };
}
